import { useCallback, useMemo, useState } from "react";
import cn from "classnames";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { Button } from "@/components/ui/buttons/button";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import { usePacks } from "@/hooks/queries/use-packs";
import { getPackDefinition } from "utils/packs/get-pack-definition";
import { PackDefinition } from "types/packs/pack-definition";
import { PlayerPackData } from "types/web-app";
import { PackRevealOverlay } from "./pack-reveal-overlay";
import { PackCardVisual } from "./pack-card-visual";

interface PackGroup {
  definition: PackDefinition;
  instances: PlayerPackData[];
}

/**
 * Packs View - the player's unopened packs, grouped by definition (core-packs C2).
 * When `embedded` (rendered as a tab panel), it drops its full-screen chrome and
 * back-action so the host view owns navigation.
 */
export const PacksView = ({ embedded = false }: { embedded?: boolean }) => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { data: packs, isLoading, isError, refetch } = usePacks();
  const [opening, setOpening] = useState<PackGroup | null>(null);

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack, !embedded);

  const groups = useMemo(() => {
    const byKey = new Map<string, PackGroup>();
    for (const pack of packs ?? []) {
      const definition = getPackDefinition(pack.pack_definition_key);
      if (!definition) continue;
      const group = byKey.get(definition.key);
      if (group) {
        group.instances.push(pack);
      } else {
        byKey.set(definition.key, { definition, instances: [pack] });
      }
    }
    return Array.from(byKey.values());
  }, [packs]);

  return (
    <div
      className={cn(
        "relative overflow-visible w-full",
        embedded
          ? ""
          : "min-h-screen bg-pk-black/80 backdrop-blur-[3px]",
      )}
    >
      <div className="flex flex-col w-full relative pb-12 overflow-visible">
        <div className="relative flex items-center justify-between w-full px-4 mt-4">
          <div className="absolute hidden xs:block h-1/2 w-full left-0 top-1/2 -translate-y-1/2 bg-pk-gray-1"></div>
          <StrokedText strokeWidth={12} fontSize={24} className="z-10">
            MY PACKS
          </StrokedText>
        </div>

        <div className="flex flex-col px-2 gap-3 mt-3 xs:mt-2">
          {isLoading && (
            <StandardFrame variant="black" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 animate-pulse">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  Loading packs...
                </StrokedText>
              </div>
            </StandardFrame>
          )}

          {!isLoading && isError && (
            <StandardFrame variant="red" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 text-center">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  Failed to load packs
                </StrokedText>
                <Button variant="red" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </StandardFrame>
          )}

          {!isLoading && !isError && groups.length > 0 && (
            <div className="max-h-[85vh] grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-4 justify-items-center grow overflow-y-auto px-1 xs:px-2 pb-2">
              {groups.map((group) => (
                <PackCard
                  key={group.definition.key}
                  group={group}
                  onOpen={() => setOpening(group)}
                />
              ))}
            </div>
          )}

          {!isLoading && !isError && groups.length === 0 && (
            <StandardFrame variant="blue" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 text-center">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  No packs yet
                </StrokedText>
                <p className="text-pk-gray-4 text-sm mt-2 max-w-sm">
                  Packs you obtain will show up here, ready to open.
                </p>
              </div>
            </StandardFrame>
          )}
        </div>
      </div>

      {opening && opening.instances[0] && (
        <PackRevealOverlay
          packInstanceId={opening.instances[0].id}
          definition={opening.definition}
          onClose={() => setOpening(null)}
        />
      )}
    </div>
  );
};

const PackCard = ({
  group,
  onOpen,
}: {
  group: PackGroup;
  onOpen: () => void;
}) => {
  const { definition, instances } = group;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative cursor-pointer" onClick={onOpen}>
        <PackCardVisual definition={definition} />
        {instances.length > 1 && (
          <span className="absolute -top-2 -right-2 z-30 text-xs font-black text-pk-black bg-pk-yellow-1 border-2 border-pk-black rounded-full min-w-6 h-6 px-1.5 flex items-center justify-center shadow-cartoon">
            x{instances.length}
          </span>
        )}
      </div>
      <Button variant="yellow" className="w-full max-w-[170px]" onClick={onOpen}>
        OPEN
      </Button>
    </div>
  );
};
